// src/components/SupplierLedgerModal.js

import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    useTheme,
    alpha,
    Grid,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Grow,
    Zoom,
} from "@mui/material";
import {
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    AccountBalanceWallet as CashIcon,
    AccountBalance as BankIcon,
    AttachMoney as FineIcon,
    LocalShipping as SupplierIcon,
} from "@mui/icons-material";

const SupplierLedgerModal = ({
    isOpen,
    onClose,
    supplier,
    fetchSupplierTransactions,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [loading, setLoading] = useState(false);
    const [expandedSection, setExpandedSection] = useState("cash");
    const [ledgerData, setLedgerData] = useState({
        rows: [],
        cash: {
            totalCredit: 0,
            totalDebit: 0,
            finalBalance: 0,
            openingBalance: 0,
            transactions: [],
        },
        bank: {
            totalCredit: 0,
            totalDebit: 0,
            finalBalance: 0,
            openingBalance: 0,
            transactions: [],
        },
        fine: {
            totalCredit: 0,
            totalDebit: 0,
            finalBalance: 0,
            openingBalance: 0,
            transactions: [],
        },
    });

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedSection(isExpanded ? panel : false);
    };

    const calculateLedger = useCallback((txns) => {
        let cashBalance = supplier?.openingBalanceCash || 0;
        let bankBalance = supplier?.openingBalanceBank || 0;
        let fineBalance = supplier?.openingBalanceFine || 0;

        let cashCredit = 0;
        let cashDebit = 0;
        let bankCredit = 0;
        let bankDebit = 0;
        let fineCredit = 0;
        let fineDebit = 0;

        const cashTransactions = [];
        const bankTransactions = [];
        const fineTransactions = [];

        let runningCashBalance = cashBalance;
        let runningBankBalance = bankBalance;
        let runningFineBalance = fineBalance;

        txns.forEach((t) => {
            const grandTotal = parseFloat(t?.summary?.grandTotal) || 0;
            const isGstApplicable = t?.transactionDetails?.gstApplicable === true;
            const paymentDetails = t?.payments || {};
            const summary = t?.summary || [];

            let cashPayment = parseFloat(paymentDetails.cash) || 0;
            let bankPayment = (parseFloat(paymentDetails.bank) || 0);
            let finePayment = parseFloat(summary?.finePayment) || 0;

            let cashCreditAmount = 0;
            let bankCreditAmount = 0;
            let fineCreditAmount = 0;

            if (isGstApplicable) {
                bankCreditAmount = grandTotal;
            } else {
                cashCreditAmount = grandTotal;
            }
            fineCreditAmount = summary?.fineBalance || 0;

            let cashDebitAmount = cashPayment;
            let bankDebitAmount = bankPayment;
            let fineDebitAmount = finePayment;

            runningCashBalance = runningCashBalance + cashCreditAmount - cashDebitAmount;
            runningBankBalance = runningBankBalance + bankCreditAmount - bankDebitAmount;
            runningFineBalance = runningFineBalance + fineCreditAmount - fineDebitAmount;

            cashCredit += cashCreditAmount;
            cashDebit += cashDebitAmount;
            bankCredit += bankCreditAmount;
            bankDebit += bankDebitAmount;
            fineCredit += fineCreditAmount;
            fineDebit += fineDebitAmount;

            const billNo = t?.transactionDetails?.billNo || '-';
            const date = t?.Date || '-';
            const type = isGstApplicable ? "Tax Invoice" : "Cash Memo";

            if (cashCreditAmount > 0 || cashDebitAmount > 0) {
                cashTransactions.push({
                    billNo,
                    date,
                    type,
                    credit: cashCreditAmount,
                    debit: cashDebitAmount,
                    balance: runningCashBalance,
                    gstApplicable: isGstApplicable,
                });
            }

            if (bankCreditAmount > 0 || bankDebitAmount > 0) {
                bankTransactions.push({
                    billNo,
                    date,
                    type,
                    credit: bankCreditAmount,
                    debit: bankDebitAmount,
                    balance: runningBankBalance,
                    gstApplicable: isGstApplicable,
                });
            }

            if (fineCreditAmount > 0 || fineDebitAmount > 0) {
                fineTransactions.push({
                    billNo,
                    date,
                    type,
                    credit: fineCreditAmount,
                    debit: fineDebitAmount,
                    balance: runningFineBalance,
                    gstApplicable: isGstApplicable,
                });
            }
        });

        setLedgerData({
            rows: txns.map((t) => ({
                billNo: t?.transactionDetails?.billNo || '-',
                date: t?.Date || '-',
                type: t?.transactionDetails?.gstApplicable ? "Tax Invoice" : "Cash Memo",
                gstApplicable: t?.transactionDetails?.gstApplicable === true,
            })),
            cash: {
                totalCredit: cashCredit.toFixed(2),
                totalDebit: cashDebit.toFixed(2),
                finalBalance: runningCashBalance.toFixed(2),
                openingBalance: supplier?.openingBalanceCash || 0,
                transactions: cashTransactions,
            },
            bank: {
                totalCredit: bankCredit.toFixed(2),
                totalDebit: bankDebit.toFixed(2),
                finalBalance: runningBankBalance.toFixed(2),
                openingBalance: supplier?.openingBalanceBank || 0,
                transactions: bankTransactions,
            },
            fine: {
                totalCredit: fineCredit.toFixed(2),
                totalDebit: fineDebit.toFixed(2),
                finalBalance: runningFineBalance.toFixed(2),
                openingBalance: supplier?.openingBalanceFine || 0,
                transactions: fineTransactions,
            },
        });
    }, [supplier]);

    const loadLedger = useCallback(async () => {
        setLoading(true);
        try {
            let data;
            if (fetchSupplierTransactions) {
                data = await fetchSupplierTransactions(supplier.id);
            } else {
                data = [];
            }
            const txns = data || [];
            calculateLedger(txns);
        } catch (error) {
            console.error("Failed to load supplier transactions:", error);
            calculateLedger([]);
        } finally {
            setLoading(false);
        }
    }, [supplier?.id, fetchSupplierTransactions, calculateLedger]);

    useEffect(() => {
        if (isOpen && supplier?.id) {
            loadLedger();
        }
    }, [isOpen, supplier?.id, loadLedger]);

    if (!isOpen) return null;

    const getBalanceColor = (balance) => {
        const numBalance = parseFloat(balance);
        if (numBalance > 0) return isDark ? "#4ade80" : "#16a34a";
        if (numBalance < 0) return isDark ? "#f87171" : "#dc2626";
        return isDark ? "#a0a0a0" : "#6b7280";
    };

    const getStatusChip = (balance) => {
        const numBalance = parseFloat(balance);
        if (numBalance > 0) {
            return (
                <Chip
                    label="Credit"
                    size="small"
                    sx={{
                        bgcolor: alpha('#4ade80', 0.2),
                        color: '#4ade80',
                        fontWeight: 600,
                        border: `1px solid ${alpha('#4ade80', 0.3)}`,
                    }}
                />
            );
        }
        if (numBalance < 0) {
            return (
                <Chip
                    label="Debit"
                    size="small"
                    sx={{
                        bgcolor: alpha('#f87171', 0.2),
                        color: '#f87171',
                        fontWeight: 600,
                        border: `1px solid ${alpha('#f87171', 0.3)}`,
                    }}
                />
            );
        }
        return (
            <Chip
                label="Zero"
                size="small"
                sx={{
                    bgcolor: alpha('#6b7280', 0.2),
                    color: '#6b7280',
                    fontWeight: 600,
                    border: `1px solid ${alpha('#6b7280', 0.3)}`,
                }}
            />
        );
    };

    const TransactionTable = ({ transactions, openingBalance, type, showFine = false }) => {
    if (transactions.length === 0) {
        return (
            <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: "14px" }}>
                    No transactions found for {type} balance
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ overflowX: "auto" }}>
            <Box
                component="table"
                sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                    "& th, & td": {
                        p: "10px 14px",
                        textAlign: "right",
                    },
                    "& th:first-of-type, & td:first-of-type": {
                        textAlign: "left",
                    },
                    "& thead": {
                        background: isDark
                            ? "linear-gradient(120deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.8))"
                            : "linear-gradient(120deg, rgba(239, 246, 255, 0.8), rgba(224, 231, 255, 0.8))",
                        backdropFilter: "blur(10px)",
                    },
                    "& th": {
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: isDark ? "#94a3b8" : "#64748b",
                        borderBottom: isDark
                            ? "1px solid rgba(255, 255, 255, 0.05)"
                            : "1px solid rgba(226, 232, 240, 0.5)",
                    },
                    "& tbody tr": {
                        transition: "background 0.2s ease",
                    },
                    "& tbody tr:nth-of-type(even)": {
                        background: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.01)",
                    },
                    "& tbody tr:hover": {
                        background: isDark ? "rgba(59, 130, 246, 0.05)" : "rgba(16, 185, 129, 0.04)",
                    },
                    "& td": {
                        color: isDark ? "#e2e8f0" : "#1e293b",
                        borderBottom: isDark
                            ? "1px solid rgba(255, 255, 255, 0.03)"
                            : "1px solid rgba(226, 232, 240, 0.3)",
                    },
                    "& .credit": {
                        color: "#4ade80",
                        fontWeight: 700,
                    },
                    "& .debit": {
                        color: "#f87171",
                        fontWeight: 700,
                    },
                    "& .gst-tag": {
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontSize: "9px",
                        fontWeight: 700,
                        background: isDark ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)",
                        color: "#3b82f6",
                        marginLeft: "6px",
                    },
                    "& .opening-row": {
                        fontWeight: 600,
                        background: isDark
                            ? "rgba(59, 130, 246, 0.05)"
                            : "rgba(59, 130, 246, 0.04)",
                    },
                }}
            >
                <thead>
                    <tr>
                        <th style={{ minWidth: "100px" }}>Bill No.</th>
                        <th style={{ minWidth: "80px" }}>Type</th>
                        <th style={{ minWidth: "100px" }}>Credit</th>
                        <th style={{ minWidth: "100px" }}>Debit</th>
                        <th style={{ minWidth: "100px" }}>Balance</th>
                    </tr>
                </thead>
                <tbody>
                    {/* Opening Balance Row */}
                    <tr className="opening-row">
                        <td>Opening</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td style={{ fontWeight: 700 }}>
                            {type === "Fine" ? `${openingBalance?.toFixed(2) || "0.00"} gms` : `₹${openingBalance?.toFixed(2) || "0.00"}`}
                        </td>
                    </tr>

                    {/* Transaction Rows */}
                    {transactions.map((row, index) => (
                        <tr key={index}>
                            <td>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                                        {row.billNo !== 'N/A' ? row.billNo : 'N/A'}
                                    </Typography>
                                    {row.gstApplicable && <span className="gst-tag">GST</span>}
                                </Box>
                            </td>
                            <td>
                                <Chip
                                    label={row.type}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                        color: isDark ? '#e2e8f0' : '#1e293b',
                                    }}
                                />
                            </td>
                            <td className="credit">
                                {type === "Fine" ? `${row.credit.toFixed(2)} gms` : `₹${row.credit.toFixed(2)}`}
                            </td>
                            <td className="debit">
                                {type === "Fine" ? `${row.debit.toFixed(2)} gms` : `₹${row.debit.toFixed(2)}`}
                            </td>
                            <td style={{ fontWeight: 700, color: getBalanceColor(row.balance) }}>
                                {type === "Fine" ? `${row.balance.toFixed(2)} gms` : `₹${row.balance.toFixed(2)}`}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Box>
        </Box>
    );
};

    const BalanceSummaryCard = ({ title, data, icon, color, type }) => {
        const isFine = type === "fine";
        const balanceValue = parseFloat(data.finalBalance);
        const openingValue = parseFloat(data.openingBalance);

        return (
            <Grow in={true} timeout={300}>
                <Card
                    elevation={0}
                    sx={{
                        minWidth: 320,
                        background: isDark
                            ? `linear-gradient(135deg, ${alpha(color, 0.1)}, rgba(30, 41, 59, 0.8))`
                            : `linear-gradient(135deg, ${alpha(color, 0.06)}, rgba(255, 255, 255, 0.9))`,
                        borderRadius: "16px",
                        border: isDark
                            ? `1px solid ${alpha(color, 0.2)}`
                            : `1px solid ${alpha(color, 0.15)}`,
                        backdropFilter: "blur(10px)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: `0 8px 25px ${alpha(color, 0.15)}`,
                            borderColor: alpha(color, 0.3),
                        },
                    }}
                >
                    <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: "12px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: `linear-gradient(135deg, ${alpha(color, 0.2)}, ${alpha(color, 0.05)})`,
                                        border: `1px solid ${alpha(color, 0.15)}`,
                                    }}
                                >
                                    {React.cloneElement(icon, { sx: { color: color, fontSize: 22 } })}
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 600,
                                            color: isDark ? "#e2e8f0" : "#1e293b",
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "11px",
                                            color: isDark ? "#94a3b8" : "#64748b",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        Opening: {isFine ? `${openingValue.toFixed(2)} gms` : `₹${openingValue.toFixed(2)}`}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ textAlign: "right" }}>
                                <Typography
                                    sx={{
                                        fontSize: "18px",
                                        fontWeight: 800,
                                        color: getBalanceColor(balanceValue),
                                    }}
                                >
                                    {isFine ? `${balanceValue.toFixed(2)} gms` : `₹${balanceValue.toFixed(2)}`}
                                </Typography>
                                {getStatusChip(balanceValue)}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grow>
        );
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            TransitionComponent={Zoom}
            PaperProps={{
                sx: {
                    background: isDark
                        ? "linear-gradient(145deg, rgba(26, 35, 50, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)"
                        : "linear-gradient(145deg, #ffffff 0%, #fafafa 100%)",
                    borderRadius: "24px",
                    border: isDark
                        ? "1px solid rgba(255, 255, 255, 0.06)"
                        : "1px solid rgba(255, 255, 255, 0.8)",
                    boxShadow: isDark
                        ? "0 25px 60px rgba(0,0,0,0.8), 0 0 40px rgba(59,130,246,0.05)"
                        : "0 25px 60px rgba(0,0,0,0.15), 0 0 40px rgba(59,130,246,0.08)",
                    backdropFilter: "blur(20px)",
                    maxWidth: "1100px",
                    width: "100%",
                    maxHeight: "92vh",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                },
            }}
            BackdropProps={{
                sx: {
                    background: "rgba(15, 23, 42, 0.35)",
                    backdropFilter: "blur(6px)",
                },
            }}
        >
            {/* Header with Premium Gradient */}
            <Box sx={{
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: isDark
                        ? 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(139,92,246,0.15) 100%)'
                        : 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(139,92,246,0.08) 100%)',
                    opacity: 0.6,
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: isDark
                        ? 'linear-gradient(90deg, #3b82f6, #8b5cf6, #3b82f6)'
                        : 'linear-gradient(90deg, #2563eb, #7c3aed, #2563eb)',
                    backgroundSize: '200% 100%',
                    animation: 'gradientMove 3s ease infinite',
                },
            }}>
                <Box sx={{
                    p: 3,
                    pb: 2.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    position: 'relative',
                    zIndex: 1,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                        <Box sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '14px',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.dark, 0.7)})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                        }}>
                            <SupplierIcon sx={{ fontSize: 24, color: '#ffffff' }} />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography sx={{
                                    fontWeight: 800,
                                    fontSize: '1.25rem',
                                    background: isDark
                                        ? 'linear-gradient(135deg, #f1f5f9 0%, #94a3b8 100%)'
                                        : 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}>
                                    Supplier Ledger
                                </Typography>
                                <Chip
                                    label={`${ledgerData.cash.transactions.length + ledgerData.bank.transactions.length + ledgerData.fine.transactions.length} Transactions`}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: '0.6rem',
                                        fontWeight: 700,
                                        background: `linear-gradient(135deg, ${alpha('#8b5cf6', 0.15)}, ${alpha('#7c3aed', 0.15)})`,
                                        color: '#8b5cf6',
                                        border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                />
                            </Box>
                            <Typography sx={{
                                fontSize: '0.85rem',
                                color: isDark ? '#94a3b8' : '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.25,
                            }}>
                                {supplier?.name} • {supplier?.gstNumber ? `GST: ${supplier.gstNumber}` : 'No GST'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: isDark ? "#94a3b8" : "#64748b",
                            background: isDark
                                ? 'rgba(255,255,255,0.05)'
                                : 'rgba(0,0,0,0.04)',
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                                background: isDark
                                    ? 'rgba(255,255,255,0.1)'
                                    : 'rgba(0,0,0,0.08)',
                                color: theme.palette.primary.main,
                                transform: 'rotate(90deg)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* Body */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    overflow: "hidden",
                    flex: 1,
                    p: "24px 28px 20px",
                }}
            >
                {loading ? (
                    <Box sx={{ textAlign: "center", py: 6 }}>
                        <CircularProgress size={48} sx={{ color: "#3b82f6", mb: 3 }} />
                        <Typography sx={{
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontSize: "16px",
                            fontWeight: 500,
                        }}>
                            Loading supplier ledger...
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Summary Cards */}
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                            <Grid item xs={12} sm={4}>
                                <BalanceSummaryCard
                                    title="Cash Balance"
                                    data={ledgerData.cash}
                                    color="#10B981"
                                    type="cash"
                                    icon={<CashIcon />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <BalanceSummaryCard
                                    title="Bank Balance"
                                    data={ledgerData.bank}
                                    color="#3B82F6"
                                    type="bank"
                                    icon={<BankIcon />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <BalanceSummaryCard
                                    title="Fine Balance"
                                    data={ledgerData.fine}
                                    color="#F59E0B"
                                    type="fine"
                                    icon={<FineIcon />}
                                />
                            </Grid>
                        </Grid>

                        {/* Accordion Sections */}
                        <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
                            {/* Cash Accordion */}
                            <Accordion
                                expanded={expandedSection === "cash"}
                                onChange={handleAccordionChange("cash")}
                                sx={{
                                    background: isDark
                                        ? "rgba(30, 41, 59, 0.6)"
                                        : "rgba(255, 255, 255, 0.6)",
                                    borderRadius: "16px !important",
                                    mb: 1.5,
                                    border: isDark
                                        ? `1px solid ${alpha('#10B981', 0.15)}`
                                        : `1px solid ${alpha('#10B981', 0.1)}`,
                                    backdropFilter: "blur(10px)",
                                    "&:before": { display: "none" },
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        borderColor: alpha('#10B981', 0.25),
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: isDark ? "#94a3b8" : "#64748b" }} />}
                                    sx={{
                                        "& .MuiAccordionSummary-content": {
                                            alignItems: "center",
                                            gap: 2,
                                        },
                                        minHeight: "56px",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                                        <Box sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: "8px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: alpha('#10B981', 0.15),
                                            color: '#10B981',
                                        }}>
                                            <CashIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                        <Typography sx={{
                                            fontWeight: 700,
                                            color: isDark ? "#e2e8f0" : "#1e293b",
                                            fontSize: "0.9rem",
                                        }}>
                                            Cash Balance
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                                        <Chip
                                            label={`Credit: ₹${ledgerData.cash.totalCredit}`}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha('#10B981', 0.15),
                                                color: '#10B981',
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 24,
                                            }}
                                        />
                                        <Chip
                                            label={`Debit: ₹${ledgerData.cash.totalDebit}`}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha('#EF4444', 0.15),
                                                color: '#EF4444',
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 24,
                                            }}
                                        />
                                        <Chip
                                            label={`Balance: ₹${ledgerData.cash.finalBalance}`}
                                            size="small"
                                            sx={{
                                                bgcolor: getBalanceColor(parseFloat(ledgerData.cash.finalBalance)),
                                                color: "#fff",
                                                fontWeight: 700,
                                                fontSize: '0.65rem',
                                                height: 24,
                                            }}
                                        />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                                    <TransactionTable
                                        transactions={ledgerData.cash.transactions}
                                        openingBalance={ledgerData.cash.openingBalance}
                                        type="Cash"
                                    />
                                </AccordionDetails>
                            </Accordion>

                            {/* Bank Accordion */}
                            <Accordion
                                expanded={expandedSection === "bank"}
                                onChange={handleAccordionChange("bank")}
                                sx={{
                                    background: isDark
                                        ? "rgba(30, 41, 59, 0.6)"
                                        : "rgba(255, 255, 255, 0.6)",
                                    borderRadius: "16px !important",
                                    mb: 1.5,
                                    border: isDark
                                        ? `1px solid ${alpha('#3B82F6', 0.15)}`
                                        : `1px solid ${alpha('#3B82F6', 0.1)}`,
                                    backdropFilter: "blur(10px)",
                                    "&:before": { display: "none" },
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        borderColor: alpha('#3B82F6', 0.25),
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: isDark ? "#94a3b8" : "#64748b" }} />}
                                    sx={{
                                        "& .MuiAccordionSummary-content": {
                                            alignItems: "center",
                                            gap: 2,
                                        },
                                        minHeight: "56px",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                                        <Box sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: "8px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: alpha('#3B82F6', 0.15),
                                            color: '#3B82F6',
                                        }}>
                                            <BankIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                        <Typography sx={{
                                            fontWeight: 700,
                                            color: isDark ? "#e2e8f0" : "#1e293b",
                                            fontSize: "0.9rem",
                                        }}>
                                            Bank Balance
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                                        <Chip
                                            label={`Credit: ₹${ledgerData.bank.totalCredit}`}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha('#10B981', 0.15),
                                                color: '#10B981',
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 24,
                                            }}
                                        />
                                        <Chip
                                            label={`Debit: ₹${ledgerData.bank.totalDebit}`}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha('#EF4444', 0.15),
                                                color: '#EF4444',
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 24,
                                            }}
                                        />
                                        <Chip
                                            label={`Balance: ₹${ledgerData.bank.finalBalance}`}
                                            size="small"
                                            sx={{
                                                bgcolor: getBalanceColor(parseFloat(ledgerData.bank.finalBalance)),
                                                color: "#fff",
                                                fontWeight: 700,
                                                fontSize: '0.65rem',
                                                height: 24,
                                            }}
                                        />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                                    <TransactionTable
                                        transactions={ledgerData.bank.transactions}
                                        openingBalance={ledgerData.bank.openingBalance}
                                        type="Bank"
                                    />
                                </AccordionDetails>
                            </Accordion>

                            {/* Fine Accordion */}
                            <Accordion
                                expanded={expandedSection === "fine"}
                                onChange={handleAccordionChange("fine")}
                                sx={{
                                    background: isDark
                                        ? "rgba(30, 41, 59, 0.6)"
                                        : "rgba(255, 255, 255, 0.6)",
                                    borderRadius: "16px !important",
                                    mb: 1.5,
                                    border: isDark
                                        ? `1px solid ${alpha('#F59E0B', 0.15)}`
                                        : `1px solid ${alpha('#F59E0B', 0.1)}`,
                                    backdropFilter: "blur(10px)",
                                    "&:before": { display: "none" },
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        borderColor: alpha('#F59E0B', 0.25),
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: isDark ? "#94a3b8" : "#64748b" }} />}
                                    sx={{
                                        "& .MuiAccordionSummary-content": {
                                            alignItems: "center",
                                            gap: 2,
                                        },
                                        minHeight: "56px",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                                        <Box sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: "8px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: alpha('#F59E0B', 0.15),
                                            color: '#F59E0B',
                                        }}>
                                            <FineIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                        <Typography sx={{
                                            fontWeight: 700,
                                            color: isDark ? "#e2e8f0" : "#1e293b",
                                            fontSize: "0.9rem",
                                        }}>
                                            Fine Balance
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                                        <Chip
                                            label={`Credit: ${ledgerData.fine.totalCredit} gms`}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha('#10B981', 0.15),
                                                color: '#10B981',
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 24,
                                            }}
                                        />
                                        <Chip
                                            label={`Debit: ${ledgerData.fine.totalDebit} gms`}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha('#EF4444', 0.15),
                                                color: '#EF4444',
                                                fontWeight: 600,
                                                fontSize: '0.65rem',
                                                height: 24,
                                            }}
                                        />
                                        <Chip
                                            label={`Balance: ${ledgerData.fine.finalBalance} gms`}
                                            size="small"
                                            sx={{
                                                bgcolor: getBalanceColor(parseFloat(ledgerData.fine.finalBalance)),
                                                color: "#fff",
                                                fontWeight: 700,
                                                fontSize: '0.65rem',
                                                height: 24,
                                            }}
                                        />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                                    <TransactionTable
                                        transactions={ledgerData.fine.transactions}
                                        openingBalance={ledgerData.fine.openingBalance}
                                        type="Fine"
                                        showFine={true}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    </>
                )}
            </Box>

            {/* CSS Animation Keyframes */}
            <style>
                {`
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
            </style>
        </Dialog>
    );
};

export default SupplierLedgerModal;